import React, { useEffect, useState } from 'react';

import { Comment, OffMarketWillingnessEvent as OffMarketWillingnessEventType } from '../../utils/types';
import ImageModal from '../ImageModal/ImageModal';
import LoadingSpinner from '../../common/LoadingSpinner';
import '../Report.scss';
import './SellAndBuyListings.scss';
import { Trans } from 'react-i18next';
import { OffMarketWillingnessEvent } from '../../FeedEvents/OffMarketWillingnessEvent';
import { CloseIcon } from '../../common/CloseIcon';

type Props = {
  isLoggedIn: boolean;
  showImageModal: string | null;
  setLoginModalVisible: (visible: boolean) => void;
  refreshFeed: () => void;
  setShowImageModal: (imageUrl: string | null) => void;
  isLoadingComments: boolean;
  comments: Comment[];
  fetchComments: (isVoteRefresh: boolean) => Promise<void>;
  couldSellSelected: boolean;
  couldBuySelected: boolean;
  setCouldSellSelected: (selected: boolean) => void;
  setCouldBuySelected: (selected: boolean) => void;
  sharedEventId?: string | null;
  setSharedEventId?: (eventId: string | null) => void;
};

const SellAndBuyListings = (props: Props) => {
  const {
    showImageModal,
    setShowImageModal,
    isLoadingComments,
    comments,
    refreshFeed,
    fetchComments,
    couldSellSelected,
    couldBuySelected,
    setCouldSellSelected,
    setCouldBuySelected,
    sharedEventId,
    setSharedEventId,
  } = props;
  const [showSharedEventModal, setShowSharedEventModal] = useState<boolean>(false);
  const filteredListings = comments.filter((comment) => comment.feed_event_type === 'OFF_MARKET_WILLINGNESS');
  const sharedEvent = sharedEventId ? filteredListings.find(event => event._id === sharedEventId) : null;

  useEffect(() => {
    if (sharedEvent && !isLoadingComments) {
      // Show the shared event modal after a short delay
      const timer = setTimeout(() => {
        setShowSharedEventModal(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [sharedEvent, isLoadingComments]);

  const handleCloseSharedEventModal = () => {
    setShowSharedEventModal(false);
    if (setSharedEventId) {
      setSharedEventId(null);
    }
    // Clear the event-id from URL
    const url = new URL(window.location.href);
    url.searchParams.delete('event-id');
    window.history.replaceState({}, '', url.toString());
  };

  return (
    <div className="sell-and-buy-listings">
      {isLoadingComments ? (
        <div className="sell-and-buy-listings__loading-spinner">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {filteredListings.length > 0 && (
            <div className="sell-and-buy-listings__comments-list">
              {filteredListings.map((comment) => {
                const isPotentialSellerAndPotentialBuyerInSameBuildingOverlapEvent =
                  comment.feed_event_type === 'SELLER_BUYER_OVERLAP';

                if (isPotentialSellerAndPotentialBuyerInSameBuildingOverlapEvent) {
                  return null;
                }

                return (
                  <OffMarketWillingnessEvent
                    key={comment._id}
                    offMarketWillingnessEvent={comment as unknown as OffMarketWillingnessEventType}
                    setShowImageModal={setShowImageModal}
                    handleRefreshFeedClick={refreshFeed}
                    fetchComments={fetchComments}
                    couldSellSelected={couldSellSelected}
                    couldBuySelected={couldBuySelected}
                    setCouldSellSelected={setCouldSellSelected}
                    setCouldBuySelected={setCouldBuySelected}
                  />
                );
              })}
              {showImageModal && (
                <ImageModal
                  closeImageModal={() => setShowImageModal(null)}
                  imageUrl={showImageModal}
                />
              )}
            </div>
          )}
        </>
      )}
      {!filteredListings.length && !isLoadingComments && (
        <div className="sell-and-buy-listings__empty-state">
          <div className="sell-and-buy-listings__empty-state__text">
              <span>
                <Trans
                  i18nKey="building.offMarket.emptyPlaceholder.firstLine"
                />
              </span>
              <span>
                <Trans
                  i18nKey="building.offMarket.emptyPlaceholder.secondLine"
                />
              </span>
          </div>
        </div>
      )}
      
      {/* Shared Event Modal */}
      {showSharedEventModal && sharedEvent && (
        <div className="sell-and-buy-listings__shared-event-modal-overlay" onClick={handleCloseSharedEventModal}>
          <div className="sell-and-buy-listings__shared-event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="sell-and-buy-listings__shared-event-modal__header">
              <div className="sell-and-buy-listings__shared-event-modal__header__title">
              <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlnsXlink="http://www.w3.org/1999/xlink"
                      viewBox="0,0,256,256"
                      width="20px"
                      height="20px"
                      fillRule="nonzero"
                    >
                      <g
                        fill="#FFFFFF"
                        fillRule="nonzero"
                        stroke="none"
                        strokeWidth="1"
                        strokeLinecap="butt"
                        strokeLinejoin="miter"
                        strokeMiterlimit="10"
                        strokeDasharray=""
                        strokeDashoffset="0"
                        fontFamily="none"
                        fontWeight="none"
                        fontSize="none"
                        textAnchor="none"
                        style={{ mixBlendMode: 'normal' }}
                      >
                        <g transform="scale(8.53333,8.53333)">
                          <path d="M23,3c-2.20914,0 -4,1.79086 -4,4c0.00178,0.28117 0.03321,0.56136 0.09375,0.83594l-9.08203,4.54102c-0.75785,-0.87251 -1.85604,-1.3746 -3.01172,-1.37695c-2.20914,0 -4,1.79086 -4,4c0,2.20914 1.79086,4 4,4c1.15606,-0.0013 2.25501,-0.5027 3.01367,-1.375l9.07617,4.53906c-0.05923,0.27472 -0.08934,0.55491 -0.08984,0.83594c0,2.20914 1.79086,4 4,4c2.20914,0 4,-1.79086 4,-4c0,-2.20914 -1.79086,-4 -4,-4c-1.15606,0.0013 -2.25501,0.5027 -3.01367,1.375l-9.07617,-4.53906c0.05923,-0.27472 0.08934,-0.55491 0.08984,-0.83594c-0.00192,-0.28051 -0.03334,-0.56005 -0.09375,-0.83398l9.08203,-4.54102c0.75821,0.87178 1.85635,1.37313 3.01172,1.375c2.20914,0 4,-1.79086 4,-4c0,-2.20914 -1.79086,-4 -4,-4z"></path>
                        </g>
                      </g>
                    </svg>
                <span>
                  <Trans i18nKey={sharedEvent.could_sell ? "building.offMarket.sharedEvent.sellTitle" : "building.offMarket.sharedEvent.buyTitle"} />
                </span>
              </div>
              <button
                className="sell-and-buy-listings__shared-event-modal__header__close"
                onClick={handleCloseSharedEventModal}
              >
                <CloseIcon size={20} color='#FFF'/>
              </button>
            </div>
            <div className="sell-and-buy-listings__shared-event-modal__content">
              <div className="sell-and-buy-listings__shared-event-modal__content__description">
                <Trans i18nKey="building.offMarket.sharedEvent.description">
                  This listing was shared with you
                </Trans>
              </div>
              <div className="sell-and-buy-listings__shared-event-modal__content__event">
                <OffMarketWillingnessEvent
                  offMarketWillingnessEvent={sharedEvent as unknown as OffMarketWillingnessEventType}
                  setShowImageModal={setShowImageModal}
                  handleRefreshFeedClick={refreshFeed}
                  fetchComments={fetchComments}
                  couldSellSelected={couldSellSelected}
                  couldBuySelected={couldBuySelected}
                  setCouldSellSelected={setCouldSellSelected}
                  setCouldBuySelected={setCouldBuySelected}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SellAndBuyListings;
